import React, { useState } from 'react';
import { useAuth } from '@/context/Auth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';

const ChangePassword: React.FC = () => {
  const { signIn, updatePassword, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: t('change_password.toast.error.title.translation'),
        description: t(
          'change_password.toast.error.passwords_mismatch.translation'
        ),
      });
      return;
    }

    const { error: signInError } = await signIn({
      email: user?.email || '',
      password: currentPassword,
    });

    if (signInError) {
      toast({
        title: t('change_password.toast.error.title.translation'),
        description: t(
          'change_password.toast.error.incorrect_current.translation'
        ),
      });
      return;
    }

    const updateError = await updatePassword(newPassword);

    if (updateError) {
      toast({
        title: t('change_password.toast.error.title.translation'),
        description: t(
          'change_password.toast.error.change_failed.translation',
          { error: updateError.message }
        ),
      });
    } else {
      toast({
        title: t('change_password.toast.success.title.translation'),
        description: t('change_password.toast.success.description.translation'),
      });
      navigate('/profile');
    }
  };

  return (
    <div className="w-full rounded-lg p-4 sm:p-6">
      <div className="flex flex-col w-full max-w-md mx-auto">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('change_password.title.translation', 'Change Password')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t(
              'change_password.description.translation',
              'Enter your current password and choose a new one.'
            )}
          </p>
        </div>

        <form
          onSubmit={handleChangePassword}
          className="flex flex-col space-y-4"
        >
          <div className="flex flex-col space-y-3">
            <div className="space-y-2">
              <Label
                htmlFor="current-password"
                className="text-sm font-medium text-foreground"
              >
                {t(
                  'change_password.form.current_password.label.translation',
                  'Current Password'
                )}
              </Label>
              <Input
                id="current-password"
                placeholder={t(
                  'change_password.form.current_password.placeholder.translation'
                )}
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                required
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="new-password"
                className="text-sm font-medium text-foreground"
              >
                {t(
                  'change_password.form.new_password.label.translation',
                  'New Password'
                )}
              </Label>
              <Input
                id="new-password"
                placeholder={t(
                  'change_password.form.new_password.placeholder.translation'
                )}
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="confirm-password"
                className="text-sm font-medium text-foreground"
              >
                {t(
                  'change_password.form.confirm_password.label.translation',
                  'Confirm New Password'
                )}
              </Label>
              <Input
                id="confirm-password"
                placeholder={t(
                  'change_password.form.confirm_password.placeholder.translation'
                )}
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                className="w-full"
              />
            </div>
          </div>

          <Button
            type="submit"
            variant="default"
            className="w-full justify-center mt-2"
          >
            {t('change_password.form.submit.translation')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
